import { Groups, Members } from '../../../../types'
import { WaterlineTestResult } from '../../../practice/waterline/types'
import { WaterlineChild, TestResult, WaterlineCalendarRow, ComplianceStatus, TestCompliance, WaterlineInventoryItem } from '../types'
import { groupBy } from 'remeda'

const getYears = (startYear: number) => {
  const years: number[] = []
  const yearCount = (new Date().getFullYear() - startYear)

  for (let i = 0; i <= yearCount; i++) {
    years.push(startYear + i)
  }

  return years
}

const getQuarter = (date: Date = new Date()) => {
  const month = date.getMonth()
  const quarter = Math.floor((month + 3) / 3)

  return quarter
}

const getStartDate = (quarter: number) => {
    const today = new Date()

    const startDateMap = new Map([
      [1, new Date(today.getFullYear() - 1, 6, 1)],
      [2, new Date(today.getFullYear() - 1, 9, 1)],
      [3, new Date(today.getFullYear(), 0, 1)],
      [4, new Date(today.getFullYear(), 3, 1)],
    ])

    const startDate = startDateMap.get(quarter)

    return startDate
}

const calculateCompliance = (tests: WaterlineTestResult[], interval: string) => {
  const defaultObj: TestResult = { status: -1, testDate: '', message: '', complianceInterval: ''}

  const compliance = tests.reduce((overallCompliance: TestResult, test: WaterlineTestResult) => {
    const previousTestDate = overallCompliance.testDate ? new Date(overallCompliance.testDate) : null
    const currentTestDate = new Date(test?.test_date ?? '')
    const formattedDate = currentTestDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric'})

    if (overallCompliance.status === -1 || (previousTestDate && currentTestDate > previousTestDate)) {
      const status = test.is_pass
        ? test.retest ? ComplianceStatus.yellow : ComplianceStatus.green
        : ComplianceStatus.red
      
      const message = test.is_pass
        ? test.retest ? TestCompliance.passedRetest : TestCompliance.passedTest
        : test.retest ? TestCompliance.failedRetest : TestCompliance.failedTest

      return {
        status: status,
        message: message,
        testDate: formattedDate,
        complianceInterval: interval,
      }
    } else if (currentTestDate === previousTestDate) {
      const isComplianceRed = overallCompliance.status === ComplianceStatus.red
      const isComplianceYellow = overallCompliance.status === ComplianceStatus.yellow

      const status = test.is_pass && !isComplianceRed
        ? ( !test.retest && !isComplianceYellow ) ? ComplianceStatus.green : ComplianceStatus.yellow
        : ComplianceStatus.red

      const message = test.is_pass && !isComplianceRed
        ? ( !test.retest && !isComplianceYellow ) ? TestCompliance.passedTest : TestCompliance.passedRetest
        : test.retest ? TestCompliance.failedRetest : TestCompliance.failedTest

      return {
        status,
        message,
        testDate: formattedDate,
        complianceInterval: interval,
      }
    }
    
    return overallCompliance
  }, defaultObj)

  return compliance
}

const sortCustomers = (customers: WaterlineCalendarRow[]) => {
  return customers.sort((a, b) => {
    return b.compliance.totalStatus - a.compliance.totalStatus
  })
}

const getCalendarData = (children: WaterlineChild[]) => {
  let calendar: WaterlineCalendarRow[] = []

  const quarters = new Map([
    [1, [1,2,3]],
    [2, [4,5,6]],
    [3, [7,8,9]],
    [4, [10,11,12]],
  ])

  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1
  const currentQuarter = getQuarter()

  const years = getYears(currentYear - 1)
  const startDate = getStartDate(currentQuarter)

  children.forEach(customer => {
    let validTests = 0
    let missedTests = 0

    const customerResults: TestResult[] = []
    const customerAlerts: WaterlineTestResult[] = []
    
    const waterlineTestResults: WaterlineTestResult[] = customer.waterline_test_results ?? []
    const members: Members[] = customer.members

    waterlineTestResults.forEach((result: WaterlineTestResult) => {
      const testDate = new Date(result?.test_date ?? '')
      const testYear = testDate.getFullYear()
      const testQuarter = getQuarter(testDate)
      const isFailedRetest = (startDate && testDate >= startDate)
        && result.retest
        && !result.is_pass
        && !result.addressed_on

      result.test_year = testYear
      result.test_quarter = testQuarter
      result.is_failed_retest = Boolean(isFailedRetest)
    })

    const yearlyTestResults = groupBy(waterlineTestResults, (result: WaterlineTestResult) => result?.test_year ?? 0)

    years.forEach(year => {
      const annualTests = yearlyTestResults[year]

      if (customer.waterline_compliance_interval === 'year') {        
        if (annualTests?.length) {
          validTests++
          const complianceCode = calculateCompliance(annualTests, customer.waterline_compliance_interval)
          customerResults.push(complianceCode)
          const testAlert: WaterlineTestResult | null = annualTests.find(result => result.is_failed_retest) ?? null
          if (testAlert) customerAlerts.push(testAlert)
        } else {
          if (currentYear === year || !(customerResults.length > 0 && customerResults[customerResults.length - 1].message !== TestCompliance.inactive)) {
            customerResults.push({ status: ComplianceStatus.gray, message: TestCompliance.inactive, complianceInterval: customer.waterline_compliance_interval })
          } else {
            missedTests++
            customerResults.push({ status: ComplianceStatus.yellow, message: TestCompliance.missedTest, complianceInterval: customer.waterline_compliance_interval })
          }
        }
      } else {
        const quarterlyTestResults = annualTests?.length
          ? groupBy(annualTests, (result: WaterlineTestResult) => result?.test_quarter ?? 0)
          : null

        quarters.forEach((monthList, quarter) => {
          const quarterlyTests = quarterlyTestResults && quarterlyTestResults[quarter]

          if (quarterlyTests?.length) {
            validTests++
            const complianceCode = calculateCompliance(quarterlyTests, customer.waterline_compliance_interval)
            customerResults.push(complianceCode)
            const testAlert: WaterlineTestResult | null = quarterlyTests.find(result => result.is_failed_retest) ?? null
            if (testAlert) customerAlerts.push(testAlert)
          } else {
            if ((currentYear === year && (monthList.includes(currentMonth) || monthList[0] > currentMonth)) || !(customerResults.length > 0 && customerResults[customerResults.length - 1].message !== TestCompliance.inactive)) {
              customerResults.push({ status: ComplianceStatus.gray, message: TestCompliance.inactive, complianceInterval: customer.waterline_compliance_interval })
            } else {
              missedTests++
              customerResults.push({ status: ComplianceStatus.yellow, message: TestCompliance.missedTest, complianceInterval: customer.waterline_compliance_interval })
            }
          }
        })
      }      
    })

    const customerDetails: WaterlineCalendarRow = {
      compliance: {
        totalStatus: validTests ? Math.round((validTests / (validTests + missedTests)) * 100) : 0,
      },
      practiceName: customer.name,
      practiceCity: customer.city.toLowerCase(),
      practiceState: customer.state.trim(),
      customerNumber: customer.customer_number,
      testResults: customerResults,
      testAlerts: customerAlerts,
      members,
    }

    calendar.push(customerDetails)
  })

  if (calendar.length) {
    calendar = sortCustomers(calendar)
  }

  return calendar
}

const getFilteredPractices = (search: string, filterByType: string, filterByGroup: Groups[], practices: WaterlineCalendarRow[]) => {
  let filtered: WaterlineCalendarRow[] = structuredClone(practices)

  if (search) {
    filtered = filtered.filter(practice => {
      const cleanSearch = search.trim().toLowerCase()
      const practiceName = practice.practiceName.trim().toLowerCase()
      const customerNumber = practice.customerNumber.trim()
      
      return practiceName.includes(cleanSearch) || customerNumber.includes(cleanSearch)
    })
  }

  if (filterByType && filterByType !== 'All results') {
    filtered = filtered.filter(practice => {
      const index = practice.testResults.findIndex((result: TestResult) => {
        return result.message === filterByType
      })
      return index !== -1
    })
  }

  if (filterByGroup.length) {
    const members = filterByGroup.flatMap((group) => group?.members.map((member) => member.customer_number));
    if (members) {
      filtered = practices.filter((customer) => members.includes(customer.customerNumber));
    }
  }

  return filtered
}

const getFilteredInventoryItems = (search: string, statusFilter: string, inventory: WaterlineInventoryItem[]) => {
  const isActive = statusFilter === 'active'

  let filtered: WaterlineInventoryItem[] = (structuredClone(inventory)).filter(item => item.active === isActive)

  if (search) {
    filtered = filtered.filter(item => {
      const cleanSearch = search.trim().toLowerCase()

      const practiceName = item.childCustomerName.toLowerCase()
      const itemName = item.name?.toLowerCase() ?? ''
      const itemNumber = item.item_number?.toLowerCase() ?? ''
      const orderNumber = item.invoice_number?.toLowerCase() ?? ''
      const roomChair = item.room?.name.toLowerCase() ?? ''
      const customerNumber = item.customer_number
      
      return practiceName.includes(cleanSearch) 
        || itemName.includes(cleanSearch)
        || itemNumber.includes(cleanSearch)
        || orderNumber.includes(cleanSearch)
        || roomChair.includes(cleanSearch)
        || customerNumber.includes(cleanSearch)
    })
  }

  return filtered
}

export {
  getYears,
  getCalendarData,
  getFilteredPractices,
  getFilteredInventoryItems,
}
